import { useState } from 'react';
import type { BossDto } from '../../lib/reference';
import type { BossPlanDto, BossClearDto } from '../../lib/bosses';
import { difficultyOptionLabel, ineligibilityReason, type CharacterForce } from '../../lib/bossGrouping';
import { IconTrash } from '../icons';
import { BossIcon } from './BossIcon';

/** Solo/Duo/Trio/4-man/5-man/6-man, index 0 = partySize 1 (REV2 §4). */
const PARTY_SIZE_LABELS = ['Solo', 'Duo', 'Trio', '4-man', '5-man', '6-man'];

interface CharacterBossRowProps {
  boss: BossDto;
  character: CharacterForce;
  characterName: string;
  plan: BossPlanDto | undefined;
  clear: BossClearDto | undefined;
  editable: boolean;
  planPending: boolean;
  clearPending: boolean;
  deletePending: boolean;
  crystalMultiplier: number;
  onSetDifficulty: (difficulty: string) => void;
  onSetPartySize: (partySize: number) => void;
  /** Row-half clicks and the "Cleared" badge all route through this — see REV2 §1/§2. */
  onSetClearedWithGold: (cleared: boolean, crystalSold: boolean) => void;
  onSetCrystalSold: (crystalSold: boolean) => void;
  onRemove: () => void;
  /**
   * REV5 (docs/specs/REV5-gollux-coins.md): only meaningful for `boss.slug === 'gollux'`. The
   * running total of `coinsEarned` across this boss's clears in the visible week, and the commit
   * handler for the coin input — both computed/wired by the caller since only it can see the full
   * `clears` list this row's single `clear` doesn't carry.
   */
  weeklyCoinsTotal?: number | undefined;
  onSetCoinsEarned?: ((coinsEarned: number) => void) | undefined;
}

/**
 * One planned boss row on a character's own boss list (REV2 — docs/specs/REV2-bosses-ux.md):
 * the whole row is one click target split left/right ("clear + sell crystal" / "clear, skip sale"),
 * with the difficulty select, party-size badges and the "Cleared"/"Crystal sold" status badges
 * layered on top as the row's authoritative controls. Deliberately does NOT reuse `BossCell` —
 * that component stays exactly as-is for the Overview matrix (grid + mobile), which this spec does
 * not touch (see the spec's "Rules" section).
 *
 * Layering: the two half-buttons are an absolutely-positioned background layer; the visible content
 * (icon/name + controls) sits on top with `pointer-events-none` on the wrapper so real clicks on
 * empty row space fall through to the half-buttons underneath, while the controls cluster opts back
 * in with `pointer-events-auto` (+ `stopPropagation`, belt-and-suspenders) so picking a difficulty,
 * a party badge, or a status badge never also fires the row action.
 */
export function CharacterBossRow({
  boss,
  character,
  characterName,
  plan,
  clear,
  editable,
  planPending,
  clearPending,
  deletePending,
  crystalMultiplier,
  onSetDifficulty,
  onSetPartySize,
  onSetClearedWithGold,
  onSetCrystalSold,
  onRemove,
  weeklyCoinsTotal,
  onSetCoinsEarned,
}: CharacterBossRowProps) {
  const cleared = clear !== undefined;
  const rowActionDisabled = !editable || clearPending || planPending;
  const goldClaimed = clear?.crystalSold ?? false;
  const partyMax = Math.min(plan?.partyMax ?? 6, 6);
  const partySize = plan?.partySize ?? 1;
  const isGollux = boss.slug === 'gollux';

  const handleLeftHalf = () => onSetClearedWithGold(!cleared, !cleared);
  const handleRightHalf = () => onSetClearedWithGold(!cleared, false);
  const handleClearedBadge = () => onSetClearedWithGold(!cleared, false);

  return (
    <li className="relative isolate overflow-hidden rounded-lg border border-border bg-bg-elevated-hover">
      {/* Row-half click targets (REV2 §1) — two real buttons filling the row, not click math. */}
      <div className="absolute inset-0 flex">
        <button
          type="button"
          onClick={handleLeftHalf}
          disabled={rowActionDisabled}
          aria-label={
            cleared
              ? `${boss.name} — unclear, for ${characterName}`
              : `${boss.name} — clear and sell crystal, for ${characterName}`
          }
          title={cleared ? `Unclear ${boss.name}` : 'Clear + sell'}
          className="group/left flex min-h-tap flex-1 items-center justify-start px-3 text-left transition-colors hover:bg-accent-dim focus-visible:bg-accent-dim focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent-border disabled:cursor-not-allowed"
        >
          <span className="text-[0.65rem] font-semibold uppercase tracking-wide text-accent-strong opacity-0 transition-opacity group-hover/left:opacity-100 group-focus-visible/left:opacity-100">
            {cleared ? 'Unclear' : 'Clear + sell'}
          </span>
        </button>
        <button
          type="button"
          onClick={handleRightHalf}
          disabled={rowActionDisabled}
          aria-label={
            cleared
              ? `${boss.name} — unclear, for ${characterName}`
              : `${boss.name} — clear, skip crystal sale, for ${characterName}`
          }
          title={cleared ? `Unclear ${boss.name}` : 'Clear, skip sale'}
          className="group/right flex min-h-tap flex-1 items-center justify-end px-3 text-right transition-colors hover:bg-bg-elevated focus-visible:bg-bg-elevated focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent-border disabled:cursor-not-allowed"
        >
          <span className="text-[0.65rem] font-semibold uppercase tracking-wide text-text-faint opacity-0 transition-opacity group-hover/right:opacity-100 group-focus-visible/right:opacity-100">
            {cleared ? 'Unclear' : 'Clear, skip sale'}
          </span>
        </button>
      </div>

      <div className="pointer-events-none relative z-10 flex flex-col gap-2 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
        <span className="flex min-w-0 items-center gap-2 text-sm text-text">
          <BossIcon slug={boss.slug} name={boss.name} />
          <span className="truncate font-medium">{boss.name}</span>
        </span>

        <div
          className="pointer-events-auto flex flex-row flex-wrap items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <select
            aria-label={`${boss.name} difficulty for ${characterName}`}
            value={plan?.difficulty ?? ''}
            disabled={!editable || planPending}
            onChange={(e) => onSetDifficulty(e.target.value)}
            className="min-h-tap w-full max-w-[10.5rem] rounded-md border border-border-strong bg-bg-elevated px-1.5 text-xs text-text transition-colors focus:border-accent focus:outline-none disabled:opacity-50"
          >
            <option value="" disabled>
              Set difficulty
            </option>
            {boss.difficulties.map((d) => {
              const reason = ineligibilityReason(character, d);
              return (
                <option key={d.difficulty} value={d.difficulty} disabled={reason !== null} title={reason ?? undefined}>
                  {difficultyOptionLabel(
                    d.difficulty,
                    d.crystalValue === null ? null : d.crystalValue * crystalMultiplier,
                  )}
                  {reason ? ' ⚠' : ''}
                </option>
              );
            })}
          </select>

          <div className="flex items-center gap-1" role="group" aria-label={`${boss.name} party size for ${characterName}`}>
            {PARTY_SIZE_LABELS.slice(0, partyMax).map((label, index) => {
              const size = index + 1;
              const selected = partySize === size;
              return (
                <button
                  key={label}
                  type="button"
                  disabled={!editable || planPending}
                  aria-pressed={selected}
                  aria-label={`${label} party size for ${boss.name}, ${characterName}`}
                  onClick={() => onSetPartySize(size)}
                  className={[
                    'flex min-h-tap min-w-tap items-center justify-center rounded-full px-2 text-[0.6rem] font-semibold uppercase tracking-wide transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                    selected
                      ? 'bg-accent-dim text-accent-strong'
                      : 'border border-border-strong bg-bg-elevated text-text-faint hover:bg-bg-elevated-hover',
                  ].join(' ')}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            disabled={rowActionDisabled}
            aria-pressed={cleared}
            aria-label={`${boss.name} cleared for ${characterName}`}
            onClick={handleClearedBadge}
            className={[
              'flex min-h-tap min-w-tap items-center gap-1 rounded-full px-2.5 text-[0.625rem] font-semibold uppercase tracking-wide transition-colors disabled:cursor-not-allowed disabled:opacity-50',
              cleared
                ? 'bg-accent-dim text-accent-strong'
                : 'border border-border-strong bg-bg-elevated text-text-faint hover:bg-bg-elevated-hover',
            ].join(' ')}
          >
            {cleared && <span aria-hidden="true">✓</span>}
            Cleared
          </button>

          <button
            type="button"
            disabled={!cleared || !editable || clearPending}
            aria-disabled={!cleared || !editable || clearPending}
            aria-pressed={goldClaimed}
            aria-label={`${boss.name} crystal sold for ${characterName}`}
            onClick={() => onSetCrystalSold(!goldClaimed)}
            className={[
              'flex min-h-tap min-w-tap items-center gap-1 rounded-full px-2.5 text-[0.625rem] font-semibold uppercase tracking-wide transition-colors disabled:cursor-not-allowed disabled:opacity-50',
              goldClaimed
                ? 'bg-crystal-dim text-crystal'
                : 'border border-border-strong bg-bg-elevated text-text-faint hover:bg-bg-elevated-hover',
            ].join(' ')}
          >
            {goldClaimed && <span aria-hidden="true">✓</span>}
            Crystal sold
          </button>

          {isGollux && cleared && onSetCoinsEarned && (
            <div className="flex items-center gap-1.5">
              <GolluxCoinsInput
                boss={boss}
                characterName={characterName}
                value={clear?.coinsEarned ?? null}
                disabled={!editable || clearPending}
                onCommit={onSetCoinsEarned}
              />
              {!!weeklyCoinsTotal && weeklyCoinsTotal > 0 && (
                <span className="whitespace-nowrap text-[0.65rem] font-medium text-text-faint">
                  {weeklyCoinsTotal} this week
                </span>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={onRemove}
            disabled={deletePending}
            aria-label={`Remove ${boss.name} from ${characterName}'s list`}
            title="Remove from list"
            className="flex min-h-tap min-w-tap shrink-0 items-center justify-center rounded-lg text-text-faint transition-colors hover:bg-bg-elevated hover:text-danger disabled:opacity-50"
          >
            <IconTrash />
          </button>
        </div>
      </div>
    </li>
  );
}

/**
 * Gollux's coin count (REV5 §Frontend): a plain-number draft, same commit-on-blur idiom as
 * `SymbolsSection`'s "symbols held" input, plus Enter-to-commit since this field is typed after a
 * clear rather than idly edited. Mounts fresh each time the row transitions to cleared (it's only
 * rendered while `cleared`), so its initial draft is always the just-loaded `value` — no separate
 * resync effect needed.
 */
function GolluxCoinsInput({
  boss,
  characterName,
  value,
  disabled,
  onCommit,
}: {
  boss: BossDto;
  characterName: string;
  value: number | null;
  disabled: boolean;
  onCommit: (coinsEarned: number) => void;
}) {
  const [draft, setDraft] = useState(value === null ? '' : String(value));

  const commit = () => {
    const parsed = Number(draft);
    if (draft.trim() !== '' && Number.isFinite(parsed) && parsed >= 0 && parsed <= 10000) {
      const rounded = Math.floor(parsed);
      setDraft(String(rounded));
      if (rounded !== value) {
        onCommit(rounded);
      }
    } else {
      setDraft(value === null ? '' : String(value));
    }
  };

  return (
    <label className="flex items-center gap-1 text-[0.65rem] font-medium text-text-faint">
      Coins
      <input
        type="number"
        inputMode="numeric"
        min={0}
        max={10000}
        disabled={disabled}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          }
        }}
        onClick={(e) => e.stopPropagation()}
        aria-label={`Gollux Coins earned for ${boss.name}, ${characterName}`}
        className="min-h-tap w-16 rounded-md border border-border-strong bg-bg-elevated px-1.5 text-xs text-text focus:border-accent focus:outline-none disabled:opacity-50"
      />
    </label>
  );
}
